import React, {useCallback, useEffect, useState} from 'react';
import AutoComplete from '../AutoComplete';
import Input from '../TextInput';
import Icon from '../Icon';
import Tags from '../Tags';
import {ICON_TYPE_SEARCH} from '../Icon/constants';
import {getClassNames} from '../../utils';
import withTheme from '../../hocs/withTheme';

type SearchBarProps = {
    data?: string[],
    className?: string,
    placeholder?: string,
    options?: string[],
    onSelect?: (val) => void,
    onSearch?: (val: string) => void,
    disableFilters?: boolean,
    dropdownClassName?: string,
    isDropdownOpen?: boolean,
    filtersText?: string,
    isMobile?: boolean,
    selected?: string[],
    labelRemove?: string,
    labelReset?: string,
};

const SearchBar = (props: SearchBarProps) => {
    const {
        data,
        selected: selectedProp,
        options: optionSource,
        placeholder = 'Start typing...',
        disableFilters,
        className: classNameProp,
        dropdownClassName,
        filtersText = 'Filters',
        onSearch,
        onSelect,
        isMobile,
        labelRemove,
        isDropdownOpen,
        labelReset = 'Reset filters',
    } = props;

    const [options, setOptions] = useState(optionSource || data || []);
    const [selected, setSelected] = useState(selectedProp || []);
    const [value, setValue] = useState('');

    useEffect(() => {
        setOptions(optionSource as string[]);
    }, [optionSource]);

    useEffect(() => {
        setOptions(data as string[]);
    }, [data]);

    useEffect(() => {
        setSelected(selectedProp as string[]);
    }, [selectedProp]);

    const onSearchAutoComplete = useCallback((val: string) => {
        setOptions(data as string[]);
        setValue(val);
        if (onSearch) {
           onSearch(val as string);
        }
    }, []);

    const onSelectAutoComplete = useCallback((val) => {
        if (selected?.includes(val)) {
            setValue('');
        } else {
            setValue('');
            setSelected((oldValue) => [...oldValue, val]);
            if (onSelect) {
                onSelect([...selected, val]);
            }
        }
    }, []);

    const handleTags = (val) => {
        setSelected(val);
        if (onSelect) {
            onSelect(val);
        }
    };

    const onInputKeyup = (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            if (value.length > 0) onSelectAutoComplete(value);
        }
    };
    const className = getClassNames('SearchBar', classNameProp);

    return (
        <div className={className}>
            <AutoComplete
                dropdownClassName={getClassNames('search-dropdown', dropdownClassName)}
                dataSource={options || []}
                value={value}
                placeholder={placeholder}
                onSearch={onSearchAutoComplete}
                onSelect={onSelectAutoComplete}
                isDropdownOpen={isDropdownOpen as boolean}
            >
                <Input
                    prefix={<Icon type={ICON_TYPE_SEARCH} className="search-icon"/>}
                    onKeyUp={onInputKeyup}
                />
            </AutoComplete>

            {(selected?.length > 0 && !disableFilters) && (
                <Tags
                    data={selected}
                    title={`${filtersText}:`}
                    onChange={handleTags}
                    isMobile={isMobile}
                    labelRemove={labelRemove}
                    labelReset={labelReset}
                />
            )}
        </div>
    );
};

export default withTheme(SearchBar);
